import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FileSignature,
  Download,
  PenTool,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  X,
  ExternalLink,
  LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { Proposal, PaginationMeta } from '../types'
import { formatCurrency } from '../utils/helpers'
import Pagination from '../components/Pagination'
import LinePreviewPopup from '../components/LinePreviewPopup'
import ProposalSignatureModal from '../components/ProposalSignatureModal'

const STATUS_CONFIG: Record<number, { label: string; color: string; icon: LucideIcon }> = {
  [-1]: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  0: { label: 'Brouillon', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  1: { label: 'Validé', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  2: { label: 'Signé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  3: { label: 'Non signé', color: 'bg-red-100 text-red-800', icon: XCircle },
}

export default function Proposals() {
  const { t } = useTranslation()

  // Detect admin role / impersonation to show "Send invitation" instead of "Sign"
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin'
  const isImpersonating = !!api.getImpersonatedCustomer()

  const [proposals, setProposals] = useState<Proposal[]>([])
  const [filteredProposals, setFilteredProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortField, setSortField] = useState<string | null>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [signingId, setSigningId] = useState<string | null>(null)
  const [signatureModalProposal, setSignatureModalProposal] = useState<Proposal | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Result banner after client initiates signature
  const [signResult, setSignResult] = useState<{
    proposalRef: string
    proposalId: string
    request_id: string
    signing_url: string
  } | null>(null)
  const [signError, setSignError] = useState<string | null>(null)

  // Polling — detect when the document is actually signed
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [pollingId, setPollingId] = useState<string | null>(null)
  const [signedBanner, setSignedBanner] = useState<string | null>(null)

  const loadProposals = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getProposals({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setProposals(list)
      setFilteredProposals(list)
      setPagination(pag)
      setError(null)
    } catch (err: unknown) {
      console.error('Erreur chargement devis:', err)
      setError(getApiErrorKey(err, 'proposals.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, limit])

  const filterAndSortProposals = useCallback(() => {
    let filtered = proposals

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.customer_ref?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      const statusNum = parseInt(statusFilter, 10)
      filtered = filtered.filter((p) => {
        const s = typeof p.status === 'string' ? parseInt(p.status, 10) : Number(p.status)
        return s === statusNum
      })
    }

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const va = (a as unknown as Record<string, unknown>)[sortField] ?? ''
        const vb = (b as unknown as Record<string, unknown>)[sortField] ?? ''
        if (typeof va === 'number' && typeof vb === 'number')
          return sortDir === 'asc' ? va - vb : vb - va
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va))
      })
    }

    setFilteredProposals(filtered)
  }, [proposals, searchTerm, statusFilter, sortField, sortDir])

  useEffect(() => {
    loadProposals()
  }, [loadProposals])
  useEffect(() => {
    filterAndSortProposals()
  }, [filterAndSortProposals])

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    },
    []
  )

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
    setPollingId(null)
  }

  const startPolling = (proposalId: string, proposalRef: string) => {
    stopPolling()
    setPollingId(proposalId)

    const poll = async () => {
      try {
        const result = await api.getProposalSignStatus(proposalId)
        if (result.status === 'signed') {
          stopPolling()
          setSignResult(null)
          setSignedBanner(proposalRef)
          reloadProposalsQuietly()
        }
      } catch {
        // silent — do not break the polling loop on network errors
      }
    }

    pollIntervalRef.current = setInterval(poll, 5000)
    pollTimeoutRef.current = setTimeout(stopPolling, 10 * 60 * 1000)
  }

  // Silent reload without full-page spinner — used after signing to refresh proposal status
  const reloadProposalsQuietly = async () => {
    try {
      const response = await api.getProposals({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setProposals(list)
      setFilteredProposals(list)
      setPagination(pag)
    } catch {
      // Silent — do not override the success banner or show a load error
    }
  }

  const handleDownloadPDF = async (proposal: Proposal) => {
    setDownloadingId(proposal.id)
    try {
      const result = await api.getProposalSignStatus(proposal.id)
      let pdfBase64: string | undefined
      if (result.status === 'signed') {
        pdfBase64 = await api.downloadProposalSignedPDF(proposal.id)
      } else {
        pdfBase64 = await api.downloadProposalPDF(proposal.id)
      }
      if (pdfBase64) {
        const link = document.createElement('a')
        link.href = `data:application/pdf;base64,${pdfBase64}`
        link.download = `${proposal.ref}.pdf`
        link.click()
      }
    } catch (err) {
      console.error('Erreur téléchargement PDF:', err)
    } finally {
      setDownloadingId(null)
    }
  }

  const handleSign = async (proposal: Proposal) => {
    setSignResult(null)
    setSignError(null)
    setSigningId(proposal.id)
    try {
      const result = await api.signProposal(proposal.id, i18n.language)
      // Open the Firma signing page in a new dedicated tab immediately
      if (result.signing_url) {
        window.open(result.signing_url, '_blank', 'noopener,noreferrer')
      }
      // Show the success banner so the user can re-open the tab if needed
      setSignResult({
        proposalRef: proposal.ref,
        proposalId: proposal.id,
        request_id: result.request_id,
        signing_url: result.signing_url,
      })
      reloadProposalsQuietly()
      startPolling(proposal.id, proposal.ref)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      console.error('Erreur signature:', apiErr)
      setSignError(getApiErrorKey(apiErr, 'proposals.signError'))
    } finally {
      setSigningId(null)
    }
  }

  const openSignatureModal = (proposal: Proposal) => setSignatureModalProposal(proposal)
  const closeSignatureModal = () => setSignatureModalProposal(null)

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR')
    } catch {
      return dateStr
    }
  }

  const getStatusConfig = (status: number | string) => {
    const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
    return (
      STATUS_CONFIG[numStatus] || {
        label: 'Inconnu',
        color: 'bg-gray-100 text-gray-800',
        icon: Clock,
      }
    )
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    const target = e.currentTarget // capture before setTimeout (React nullifies currentTarget after handler)
    hoverTimer.current = setTimeout(() => {
      setHoveredId(id)
      setHoveredRect(target.getBoundingClientRect())
    }, 400)
  }

  const handleRowMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoveredId(null)
    setHoveredRect(null)
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300 inline ml-1" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-primary-600 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-primary-600 inline ml-1" />
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-gray-900">Devis</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 mb-4">{t(error)}</p>
          <button
            onClick={loadProposals}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* "Document signed" banner — shown when polling detects signature completion */}
      {signedBanner && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-semibold">
              {t('proposals.signedTitle', { ref: signedBanner })}
            </p>
            <p className="text-green-700 text-sm mt-1">{t('proposals.signedBody')}</p>
          </div>
          <button
            onClick={() => setSignedBanner(null)}
            className="text-green-500 hover:text-green-700 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Success banner — shown after signature request is created */}
      {signResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-semibold">
              {t('proposals.signSuccessTitle', { ref: signResult.proposalRef })}
            </p>
            <p className="text-green-700 text-sm mt-1">{t('proposals.signSuccessBody')}</p>
            {signResult.signing_url && (
              <a
                href={signResult.signing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                {t('proposals.signNow')}
              </a>
            )}
            {pollingId === signResult.proposalId && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-green-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('proposals.signPolling', { ref: signResult.proposalRef })}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              setSignResult(null)
              stopPolling()
            }}
            className="text-green-500 hover:text-green-700 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error banner — shown when signature request creation fails */}
      {signError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="flex-1 text-red-700 text-sm">{t(signError)}</p>
          <button
            onClick={() => setSignError(null)}
            className="text-red-400 hover:text-red-600 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <p className="text-sm font-medium text-gray-600">{t('proposals.title')}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">
          {pagination ? pagination.total : proposals.length}
        </p>
      </div>

      {!pagination && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('proposals.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none appearance-none bg-white"
              >
                <option value="all">{t('common.allStatuses')}</option>
                <option value="-1">{t('proposals.statusCancelled')}</option>
                <option value="0">{t('proposals.statusDraft')}</option>
                <option value="1">{t('proposals.statusValidated')}</option>
                <option value="2">{t('proposals.statusSigned')}</option>
                <option value="3">{t('proposals.statusUnsigned')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {proposals.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileSignature className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">{t('proposals.notFound')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {filteredProposals.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <FileSignature className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>{t('proposals.noneFound')}</p>
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 sm:hidden">
                {filteredProposals.map((proposal) => {
                  const statusConfig = getStatusConfig(proposal.status)
                  const StatusIcon = statusConfig.icon
                  const canSign = Number(proposal.status) === 1
                  return (
                    <li key={proposal.id} className="p-4 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {pagination && (
                            <p className="text-xs text-gray-500 truncate">
                              {proposal.customer_name ?? '—'}
                            </p>
                          )}
                          <Link
                            to={`/proposals/${proposal.id}${proposal._erp_id ? `?erp_id=${proposal._erp_id}` : ''}`}
                            className="font-medium text-primary-600 hover:text-primary-700 text-sm"
                          >
                            {proposal.ref}
                          </Link>
                        </div>
                        <span
                          className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {proposal.status_label || statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                        <span>{formatDate(proposal.date)}</span>
                        <span className="font-semibold text-sm text-gray-900">
                          {formatCurrency(proposal.total_incl_tax)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-400">
                          {t('proposals.colValidity')} : {formatDate(proposal.end_validity_date)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleDownloadPDF(proposal)}
                            disabled={downloadingId === proposal.id}
                            className="text-gray-500 hover:text-primary-600 transition-colors"
                            title="Télécharger le PDF"
                          >
                            {downloadingId === proposal.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                          {canSign && (isAdmin || isImpersonating) && (
                            <button
                              onClick={() => openSignatureModal(proposal)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                            >
                              <Send className="w-3 h-3" />
                              {t('proposalDetail.sendForSignature')}
                            </button>
                          )}
                          {canSign && !isAdmin && !isImpersonating && (
                            <button
                              onClick={() => handleSign(proposal)}
                              disabled={signingId === proposal.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {signingId === proposal.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <PenTool className="w-3 h-3" />
                              )}
                              {t('proposalDetail.sign')}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {pagination && (
                        <th
                          className="text-left py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('customer_name')}
                        >
                          {t('common.client')} <SortIcon field="customer_name" />
                        </th>
                      )}
                      <th
                        className="text-left py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('ref')}
                      >
                        {t('proposals.colRef')} <SortIcon field="ref" />
                      </th>
                      <th
                        className="text-left py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('date')}
                      >
                        {t('proposals.colDate')} <SortIcon field="date" />
                      </th>
                      <th
                        className="text-left py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('end_validity_date')}
                      >
                        {t('proposals.colValidity')} <SortIcon field="end_validity_date" />
                      </th>
                      <th
                        className="text-right py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('total_excl_tax')}
                      >
                        {t('proposals.colAmountExcl')} <SortIcon field="total_excl_tax" />
                      </th>
                      <th
                        className="text-right py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('total_incl_tax')}
                      >
                        {t('proposals.colAmountIncl')} <SortIcon field="total_incl_tax" />
                      </th>
                      <th
                        className="text-center py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        {t('proposals.colStatus')} <SortIcon field="status" />
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">
                        {t('proposals.colActions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProposals.map((proposal) => {
                      const statusConfig = getStatusConfig(proposal.status)
                      const StatusIcon = statusConfig.icon
                      const canSign = Number(proposal.status) === 1 // Validé

                      return (
                        <tr
                          key={proposal.id}
                          className="hover:bg-gray-50 transition-colors"
                          onMouseEnter={(e) => handleRowMouseEnter(e, proposal.id)}
                          onMouseLeave={handleRowMouseLeave}
                        >
                          {pagination && (
                            <td className="py-3 px-4 text-sm text-gray-700 font-medium whitespace-nowrap">
                              {proposal.customer_name ?? '—'}
                            </td>
                          )}
                          <td className="py-3 px-4">
                            <Link
                              to={`/proposals/${proposal.id}${proposal._erp_id ? `?erp_id=${proposal._erp_id}` : ''}`}
                              className="font-medium text-primary-600 hover:text-primary-700"
                            >
                              {proposal.ref}
                            </Link>
                            {proposal.customer_ref && (
                              <p className="text-xs text-gray-500">
                                Réf. client: {proposal.customer_ref}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {formatDate(proposal.date)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {formatDate(proposal.end_validity_date)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-900">
                            {formatCurrency(proposal.total_excl_tax)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                            {formatCurrency(proposal.total_incl_tax)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}
                            >
                              <StatusIcon className="w-3.5 h-3.5" />
                              {proposal.status_label || statusConfig.label}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              {/* Télécharger PDF */}
                              <button
                                onClick={() => handleDownloadPDF(proposal)}
                                disabled={downloadingId === proposal.id}
                                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Télécharger le PDF"
                              >
                                {downloadingId === proposal.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </button>

                              {/* Send invitation: admin or impersonating user → opens contact selection modal */}
                              {canSign && (isAdmin || isImpersonating) && (
                                <button
                                  onClick={() => openSignatureModal(proposal)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                                  title={t('proposalDetail.sendForSignature')}
                                >
                                  <Send className="w-3.5 h-3.5" />
                                  {t('proposalDetail.sendForSignature')}
                                </button>
                              )}
                              {/* Sign: regular customer only */}
                              {canSign && !isAdmin && !isImpersonating && (
                                <button
                                  onClick={() => handleSign(proposal)}
                                  disabled={signingId === proposal.id}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                                  title="Signer le devis"
                                >
                                  {signingId === proposal.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <PenTool className="w-3.5 h-3.5" />
                                  )}
                                  {t('proposalDetail.sign')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {pagination && (
            <div className="px-6 py-4">
              <Pagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Hover line preview */}
      {hoveredId && hoveredRect && (
        <LinePreviewPopup id={hoveredId} type="proposal" anchorRect={hoveredRect} />
      )}

      {/* Contact selection modal for signature invitation */}
      {signatureModalProposal && (
        <ProposalSignatureModal
          proposalId={signatureModalProposal.id}
          proposalRef={signatureModalProposal.ref}
          onClose={closeSignatureModal}
          onSuccess={() => {
            closeSignatureModal()
            loadProposals()
          }}
        />
      )}
    </div>
  )
}
